import jsPDF from "jspdf";
import { pdfDocSections } from "../_constants/docSections";
import { calculateAspectRatioFit } from "./generalUtils";

class PdfDocument {
  constructor(sections) {
    // Printable box for Letter-size: (15, 25) x (590, 760)
    this.pdfProps = {
      orientation: "portrait",
      unit: "pt",
      format: "letter",
    };
    this.sections = sections || [];
    this.images = [];
    this.imageData = [];
    this.cursorY = 0;
    this.maxY = 750;

    // Bind to allow externally-sourced render funcs access to PdfDocument members
    this.sections.forEach((s) => (s.renderSection = s.renderSection.bind(this)));
    // this.createPdf = this.createPdf.bind(this);
  }

  _renderHeader(pdf) {
    const header = this.sections.find((s) => s.type === pdfDocSections.PDF_DOC_HEADER);
    if (header !== undefined) header.renderSection(pdf);
    // cursorY is re-set here
    else this.cursorY = 25; // If no header exists, reset cursorY back to top of page.
  }

  _renderFooter(pdf) {
    const footer = this.sections.find((s) => s.type === pdfDocSections.PDF_DOC_FOOTER);
    if (footer !== undefined) footer.renderSection(pdf);
  }

  _renderSignature(pdf) {
    const sigBlock = this.sections.find(
      (s) => s.type === pdfDocSections.PDF_DOC_SIGNATURE
    );
    if (sigBlock !== undefined) sigBlock.renderSection(pdf);
  }

  _addPage(pdf) {
    pdf.addPage();
    this._setDefaults(pdf);
    this._renderHeader(pdf);
    this._renderFooter(pdf);
  }

  _setDefaults(pdf) {
    pdf.setFont("helvetica", "normal").setFontSize(11);
    pdf.setLineWidth(0.7);
  }

  /**
   * Load all images based on the array of URLs found in this.images
   * @returns {Promise}
   */
  _loadAllImages() {
    return Promise.all(this.images.map((img) => this._loadImage(img))).catch((imageUrl) =>
      console.log("Cannot load image: " + imageUrl)
    );
  }

  /**
   * Loads an image from the provided image URL,
   * and collects its dataURL in the 'imageData' class member.
   * @param {import("../components/adr/adrUtils/adrUtils").ImagePayload} img
   * @param {Function} lastImgCallback
   */
  _loadImage(img) {
    return new Promise((resolve, reject) => {
      let image = new Image();
      image.onerror = () => reject();
      image.onload = () => {
        let canvas = document.createElement("canvas");
        let newDim = calculateAspectRatioFit(image.width, image.height, 200, 150);
        document.body.appendChild(canvas);
        canvas.width = 200;
        canvas.height = 150;

        let ctx = canvas.getContext("2d");
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, newDim.width, newDim.height);

        // Grab the image as a jpeg encoded in base64, but only the data
        let dataUrl = canvas
          .toDataURL("image/jpeg")
          .slice("data:image/jpeg;base64,".length);

        document.body.removeChild(canvas);

        this.imageData = [...this.imageData, { dataUrl, context: img.context }];
        resolve();
      };
      image.src = img.imageUrl;
    });
  }

  addSection(section) {
    this.sections.push(section);
  }

  addImage(image) {
    this.images.push(image);
  }

  renderPdf(previewOnly = false) {
    var pdfDoc = new jsPDF(this.pdfProps);

    // Loading images is an async operation, so we must finish loading
    // all images to be rendered prior to calling render funcs.
    // Once this._loadAllImages is run, all dataUrl-ified images are
    // available in the this.imageData array, accessible from any section
    // renderer.
    this._loadAllImages().then(() => {
      // Get page number render func if one is supplied.
      // If not, it remains 'undefined'
      const pageNoSection = this.sections.find(
        (s) => s.type === pdfDocSections.PDF_DOC_PAGENUMBER
      );

      this._setDefaults(pdfDoc);
      this._renderHeader(pdfDoc);
      this._renderFooter(pdfDoc);

      // Call render funcs in each section
      this.sections.forEach((section) => {
        if (section.type === pdfDocSections.PDF_DOC_BODY) section.renderSection(pdfDoc);
      });

      if (pageNoSection !== undefined) {
        for (let i = 1; i <= pdfDoc.internal.getNumberOfPages(); i++) {
          pdfDoc.setPage(i);
          pageNoSection.renderSection(pdfDoc);
        }
      }

      // pdfDoc.output("dataurlnewwindow");

      if (previewOnly) window.open(pdfDoc.output("bloburl"));
      else pdfDoc.save("file.pdf");
      // FileSaver.saveAs(pdfDoc.output('blob'), "generated-file.pdf");
      // window.open(pdfDoc.output("bloburl", { filename: "generated-file.pdf" }));
    });
  }
}

export default PdfDocument;
